import { createFileRoute, Link } from '@tanstack/react-router'
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button"
import { ScrollText, UserCheck, ShieldCheck, CreditCard, Scale, AlertCircle, FileSignature, Gavel, Mail, ArrowLeft } from "lucide-react"
import { useTranslation } from "@/lib/i18n"

import { createLLMOMeta, createWebPageSchema, createBreadcrumbSchema } from '@/lib/seo'

export const Route = createFileRoute('/terms')({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: 'Terms of Service - Zapify',
        description: 'The rules and guidelines for using Zapify. Learn about eligibility, acceptable use, subscriptions, privacy, and liability.',
        url: 'https://zapify.pro/terms',
        publishedTime: '2024-12-28T00:00:00Z',
      }),
      { name: "keywords", content: "terms of service, terms, zapify terms, acceptable use" },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:description", content: "The rules and guidelines for using Zapify. Learn about eligibility, acceptable use, subscriptions, privacy, and liability." },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/terms" }
    ],
    scripts: [
      createWebPageSchema({
        name: 'Terms of Service - Zapify',
        description: 'The rules and guidelines for using Zapify. Learn about eligibility, acceptable use, subscriptions, privacy, and liability.',
        url: 'https://zapify.pro/terms',
      }),
      createBreadcrumbSchema({
        itemListElement: [
          {
            position: 1,
            name: 'Home',
            item: 'https://zapify.pro',
          },
          {
            position: 2,
            name: 'Terms of Service',
            item: 'https://zapify.pro/terms',
          },
        ],
      }),
    ],
  }),
  component: TermsPage,
})

export function TermsPage() {
    const { translations: t } = useTranslation();
    return (
        <main className="min-h-screen bg-white dark:bg-black py-20 px-4 md:px-8 transition-colors">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center space-y-4">
              <Badge variant="outline" className="px-4 py-1 border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 mb-2">
                {t.legal.lastUpdated}: December 28, 2024
              </Badge>
              <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400">
                {t.legal.termsOfService}
              </h1>
              <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
                Please read these terms carefully before using Zapify. By using our
                service, you agree to be bound by these terms.
              </p>
            </div>
    
            <Card className="border-gray-200 dark:border-neutral-800 shadow-lg bg-white dark:bg-neutral-900">
              <CardContent className="p-8 md:p-12 space-y-12">
                <div className="prose prose-gray dark:prose-invert max-w-none space-y-12">
                  
                  {/* Introduction */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                        <ScrollText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        1. Introduction
                      </h2>
                    </div>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 leading-relaxed ml-11">
                      <p>
                        Welcome to Zapify ("we," "our," or "us"). By accessing or
                        using our website, mobile application, and services
                        (collectively, the "Service"), you agree to be bound by
                        these Terms of Service ("Terms").
                      </p>
                      <p>
                        If you disagree with any part of these terms, you may not
                        access the Service.
                      </p>
                    </div>
                  </section>
    
                  <Separator className="bg-gray-100 dark:bg-neutral-800" />
    
                  {/* User Accounts */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <UserCheck className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        2. User Accounts
                      </h2>
                    </div>
                    <ul className="grid gap-3 text-gray-700 dark:text-gray-300 ml-11 list-disc pl-4">
                      <li>
                        You must provide accurate, complete, and current information
                        upon registration
                      </li>
                      <li>
                        You are responsible for maintaining the confidentiality of
                        your account and password
                      </li>
                      <li>
                        You accept responsibility for all activities that occur
                        under your account
                      </li>
                      <li>
                        You must notify us immediately upon becoming aware of any
                        breach of security
                      </li>
                    </ul>
                  </section>
    
                  {/* Acceptable Use */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <ShieldCheck className="w-6 h-6 text-green-600 dark:text-green-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        3. Acceptable Use
                      </h2>
                    </div>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                      <p>You agree not to use the Service:</p>
                      <ul className="grid gap-3 list-disc pl-4">
                        <li>
                          In any way that violates any applicable national or
                          international law or regulation
                        </li>
                        <li>
                          To transmit, or procure the sending of, any advertising or
                          promotional material (spam)
                        </li>
                        <li>
                          To impersonate or attempt to impersonate the Company, a
                          Company employee, another user, or any other person or
                          entity
                        </li>
                        <li>
                          To engage in any other conduct that restricts or inhibits
                          anyone's use or enjoyment of the Service
                        </li>
                      </ul>
                    </div>
                  </section>
    
                  <Separator className="bg-gray-100 dark:bg-neutral-800" />

                  {/* Service Availability */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                            <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                            4. Service Availability
                        </h2>
                    </div>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                        <p>
                            We strive to ensure our services are available 24/7, but we do not guarantee uninterrupted access. We reserve the right to suspend or withdraw service without notice for maintenance or other reasons.
                        </p>
                    </div>
                  </section>
    
                  {/* Payments */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                        <CreditCard className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        5. Payments & Subscriptions
                      </h2>
                    </div>
                    <ul className="grid gap-3 text-gray-700 dark:text-gray-300 ml-11 list-disc pl-4">
                      <li>
                        All payments are processed securely through our trusted
                        payment partners
                      </li>
                      <li>
                        Subscription fees are billed in advance on a recurring basis
                      </li>
                      <li>
                        You authorize us to charge your payment method for all
                        applicable fees
                      </li>
                      <li>Price changes will be communicated 30 days in advance</li>
                      <li>
                        Refunds are subject to our{" "}
                        <Link
                          to="/refund-policy"
                          className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 decoration-2 underline-offset-2"
                        >
                          Refund Policy
                        </Link>
                      </li>
                      <li>
                        You may cancel your subscription at any time through your
                        account settings
                      </li>
                    </ul>
                  </section>
    
                  {/* Intellectual Property */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <FileSignature className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        6. Intellectual Property
                      </h2>
                    </div>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                      <p>
                        Zapify and its content, features, and functionality are
                        owned by us and are protected by international copyright,
                        trademark, and other intellectual property laws.
                      </p>
                      <p>
                        You retain ownership of any content you create using our
                        services, but grant us a license to use, store, and process
                        such content to provide our services.
                      </p>
                    </div>
                  </section>

                   <Separator className="bg-gray-100 dark:bg-neutral-800" />
    
                  {/* Privacy & Data */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                        <ShieldCheck className="w-6 h-6 text-teal-600 dark:text-teal-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        7. Privacy & Data Protection
                      </h2>
                    </div>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                      <p>
                        Your privacy is important to us. Our collection and use of
                        personal information is governed by our{" "}
                        <Link
                          to="/privacy"
                          className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 decoration-2 underline-offset-2"
                        >
                          Privacy Policy
                        </Link>
                        , which is incorporated into these Terms by reference.
                      </p>
                      <p>
                        We implement appropriate security measures to protect your
                        data, but cannot guarantee absolute security.
                      </p>
                    </div>
                  </section>
    
                  {/* Limitation of Liability */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        8. Limitation of Liability
                      </h2>
                    </div>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                      <p>
                        Our services are provided "as is" without warranties of any
                        kind. To the maximum extent permitted by law, we disclaim
                        all warranties and shall not be liable for any indirect,
                        incidental, or consequential damages.
                      </p>
                      <p>
                        Our total liability shall not exceed the amount you paid for
                        our services in the 12 months preceding the claim.
                      </p>
                    </div>
                  </section>
    
                  {/* Termination */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                        <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        9. Termination
                      </h2>
                    </div>
                    <ul className="grid gap-3 text-gray-700 dark:text-gray-300 ml-11 list-disc pl-4">
                      <li>
                        You may terminate your account at any time by contacting us
                      </li>
                      <li>
                        We may suspend or terminate your account for violations of
                        these Terms
                      </li>
                      <li>
                        Upon termination, your right to use our services ceases
                        immediately
                      </li>
                      <li>
                        We will retain your data according to our data retention
                        policies
                      </li>
                    </ul>
                  </section>
    
                  {/* Changes to Terms */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        <Scale className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        10. Changes to Terms
                      </h2>
                    </div>
                    <div className="space-y-4 text-gray-700 dark:text-gray-300 ml-11">
                      <p>
                        We reserve the right to modify these Terms at any time. We
                        will notify you of significant changes via email or through
                        our service.
                      </p>
                      <p>
                        Your continued use of our services after changes become
                        effective constitutes acceptance of the new Terms.
                      </p>
                    </div>
                  </section>
    
                  {/* Governing Law */}
                  <section>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg">
                        <Gavel className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white m-0">
                        11. Governing Law
                      </h2>
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed ml-11">
                      These Terms are governed by and construed in accordance with
                      the laws of [Your Jurisdiction], without regard to conflict of
                      law principles.
                    </p>
                  </section>
                </div>
    
                <Separator className="bg-gray-100 dark:bg-neutral-800" />
    
                {/* Contact Us */}
                <section className="text-center bg-gray-50 dark:bg-neutral-800/50 rounded-2xl p-8 border border-gray-100 dark:border-neutral-800">
                    <div className="flex flex-col items-center gap-3 mb-4">
                        <div className="p-3 bg-white dark:bg-neutral-800 rounded-full shadow-sm">
                            <Mail className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t.legal.haveQuestions}</h3>
                    </div>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t.legal.contactEmail}{' '}
                    <a
                      href="mailto:support@zapify.pro"
                      className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 font-medium"
                    >
                      support@zapify.pro
                    </a>
                    .
                  </p>
                </section>
    
                {/* Back to top */}
                <div className="text-center pt-8">
                  <Link to="/">
                    <Button size="lg" variant="gradient-brand">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      {t.legal.returnToWebsite}
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      );
}
